import { defineStore } from 'pinia';
import { isAxiosError } from 'axios';
import { persist } from '@/stores';
import bridge from '@thermenresorts/bridge';
import { useLoadingStore } from '@/stores/useLoadingStore';
import { useValidationStore } from '@/stores/useValidationStore';
import api from '@/api';
import { type CustomerStoreState } from '@/stores/schemas';

export const useCustomerStore = defineStore('customer', {
	state: (): CustomerStoreState => ({
		personal: {
			birth_date: persist('customer/personal/birth_date', null),
			email: persist('customer/personal/email', null),
			first_name: persist('customer/personal/first_name', null),
			gender: persist('customer/personal/gender', 'unknown'),
			infix: persist('customer/personal/infix', null),
			last_name: persist('customer/personal/last_name', null),
			phone_number: persist('customer/personal/phone_number', null),
			address: {
				city: persist('customer/personal/city', null),
				country: persist('customer/personal/country', null),
				house_number: persist('customer/personal/house_number', null),
				house_number_addition: persist('customer/personal/house_number_addition', null),
				street: persist('customer/personal/street', null),
				zip_code: persist('customer/personal/zip_code', null),
				attempted_lookup: persist('customer/personal/attempted_lookup', false),
			},
			opt_in: persist('customer/personal/opt_in', false),
			thermen_fans: persist('customer/personal/thermen_fans', false),
			invoice: {
				opt_in: persist('customer/personal/invoice/opt_in', false),
			},
		},
		terms: false, // Do not persist, customer must check this explicit i.c.o. changes in terms.
		comment: persist('customer/comment', null),
		authentication: {
			action: persist('customer/action', 'guest'),
			access_token: persist('customer/access_token', null),
			email: null,
			password: null,
			password_confirmation: null,
		},
	}),

	getters: {
		hasComment(state) {
			return !!state.comment;
		},

		hasBirthDate(state) {
			return !!state.personal.birth_date;
		},

		isAuthenticated(state) {
			return state.authentication.action === 'login' && !!state.authentication.access_token;
		},
	},

	actions: {
		logout() {
			this.authentication.access_token = null;
			api.client.delAccessToken();
		},

		resetPersonalDetails() {
			this.personal.birth_date = null;
			this.personal.email = null;
			this.personal.first_name = null;
			this.personal.gender = 'unknown';
			this.personal.infix = null;
			this.personal.last_name = null;
			this.personal.phone_number = null;
			this.personal.address.city = null;
			this.personal.address.country = null;
			this.personal.address.house_number = null;
			this.personal.address.house_number_addition = null;
			this.personal.address.street = null;
			this.personal.address.zip_code = null;
			this.personal.address.attempted_lookup = false;
		},

		async loginWithAccessToken(access_token: string) {
			const loadingStore = useLoadingStore();

			const loaders = loadingStore.loading('login');

			this.authentication.access_token = access_token;
			api.client.setAccessToken(this.authentication.access_token);

			try {
				const response = await api.account.me();
				this.personal.birth_date = response.member.birth_date;
				this.personal.email = response.member.email;
				this.personal.first_name = response.member.first_name;
				this.personal.gender = response.member.gender;
				this.personal.infix = response.member.infix;
				this.personal.last_name = response.member.last_name;
				this.personal.phone_number = response.member.phone_number;
				this.personal.address.city = response.member.address?.city || '';
				this.personal.address.country = response.member.address?.country || '';
				this.personal.address.house_number = response.member.address?.house_number || '';
				this.personal.address.house_number_addition = response.member.address?.house_number_addition || '';
				this.personal.address.street = response.member.address?.street || '';
				this.personal.address.zip_code = response.member.address?.zip_code || '';
				this.personal.address.attempted_lookup = true;
				this.authentication.action = 'login';

				return true;
			} catch (error) {
				console.log(error);
				this.resetPersonalDetails();
				this.authentication.access_token = null;
				this.authentication.action = 'guest';
				api.client.delAccessToken();

				return false;
			} finally {
				loadingStore.unloading(...loaders);
			}
		},

		async login() {
			const validationStore = useValidationStore();
			const loadingStore = useLoadingStore();

			const loaders = loadingStore.loading('login');

			try {
				const response = await api.account.login(this.authentication.email ?? '', this.authentication.password ?? '');
				this.personal.birth_date = response.member.birth_date ?? null;
				this.personal.email = response.member.email;
				this.personal.first_name = response.member.first_name ?? null;
				this.personal.gender = response.member.gender ?? null;
				this.personal.infix = response.member.infix ?? null;
				this.personal.last_name = response.member.last_name ?? null;
				this.personal.phone_number = response.member.phone_number ?? null;
				this.personal.address.city = response.member.address?.city || '';
				this.personal.address.country = response.member.address?.country || '';
				this.personal.address.house_number = response.member.address?.house_number || '';
				this.personal.address.house_number_addition = response.member.address?.house_number_addition || '';
				this.personal.address.street = response.member.address?.street || '';
				this.personal.address.zip_code = response.member.address?.zip_code || '';
				this.personal.address.attempted_lookup = true;
				this.authentication.access_token = response.authorization.access_token;

				api.client.setAccessToken(this.authentication.access_token);

				// send the access_token to over the bridge
				bridge.sendMessage('reservation-flow', 'reservation-flow.authenticated', {
					access_token: this.authentication.access_token,
				});

				validationStore.clearErrors();

				this.authentication.password = null;

				return true;
			} catch (error) {
				if (isAxiosError(error) && error.response?.data?.errors) {
					validationStore.setErrors(error.response.data.errors);
				}
				if (isAxiosError(error) && error.response?.status === 401) {
					validationStore.setErrors({ authentication: [error.response?.data.message] });
				}

				throw error;
			} finally {
				loadingStore.unloading(...loaders);
			}
		},

		async forgotPassword() {
			if (!this.authentication.email) return;

			const response = await api.account.forgotPassword(this.authentication.email);
			return response.status;
		},
	},
});
